import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { PickerOption } from '../../catalog'

/**
 * Typeahead bound to a catalog.
 *
 * The invariant that matters: the text the user types is only ever a *filter*. It
 * is never committed as the value. `onChange` fires with an id from `options` and
 * nothing else, so a mistyped item name cannot reach the JSON and abort a Unity
 * import.
 */
export function Combobox({
  value,
  options,
  onChange,
  placeholder = 'Search…',
  invalid = false,
  ariaLabel,
}: {
  value: string
  options: PickerOption[]
  onChange: (id: string) => void
  placeholder?: string
  invalid?: boolean
  ariaLabel: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((option) => option.id === value)
  const matches = useMemo(() => rank(options, query), [options, query])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => setHighlight(0), [query])

  function close() {
    setOpen(false)
    setQuery('')
  }

  function commit(option: PickerOption | undefined) {
    if (!option) return
    onChange(option.id)
    close()
    inputRef.current?.blur()
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1
      setHighlight((current) => {
        if (matches.length === 0) return 0
        return (current + delta + matches.length) % matches.length
      })
    } else if (event.key === 'Enter') {
      if (open) {
        event.preventDefault()
        commit(matches[highlight])
      }
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault()
        close()
      }
    } else if (event.key === 'Tab') {
      close()
    }
  }

  // An id that isn't in the catalog still has to be visible so it can be fixed.
  const orphaned = value !== '' && !selected

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        className={[
          'w-full rounded border px-2 py-1 text-sm font-mono',
          'bg-ink-lift focus:outline-none focus:ring-2 focus:ring-gold',
          invalid || orphaned ? 'border-danger bg-danger-bg' : 'border-edge',
        ].join(' ')}
        value={open ? query : (selected?.label ?? value)}
        placeholder={placeholder}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {!open && selected && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-dim">
          {selected.meta}
        </span>
      )}
      {!open && orphaned && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-danger">
          not in catalog
        </span>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full min-w-[22rem] overflow-y-auto rounded border border-edge bg-ink-lift shadow-lg"
        >
          {matches.length === 0 && (
            <li className="px-2 py-2 text-sm text-dim">No match in the catalog.</li>
          )}
          {matches.map((option, index) => (
            <li
              key={option.id}
              role="option"
              aria-selected={index === highlight}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={(event) => {
                event.preventDefault()
                commit(option)
              }}
              className={[
                'flex cursor-pointer items-baseline justify-between gap-3 px-2 py-1.5 text-sm',
                index === highlight ? 'bg-panel-lit' : '',
              ].join(' ')}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{option.label}</span>
                <span className="block truncate font-mono text-[11px] text-dim">
                  {option.id}
                </span>
              </span>
              {option.warn && (
                <span className="shrink-0 rounded bg-warn-bg px-1 text-[10px] font-semibold uppercase text-warn">
                  {option.warn}
                </span>
              )}
              <span className="shrink-0 whitespace-nowrap font-mono text-xs tabular-nums text-dim">
                {option.meta}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Subsequence match over id + label + keywords, scored so that prefix hits on the
 * label beat scattered matches deep in a tag list.
 */
function rank(options: PickerOption[], query: string): PickerOption[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return options.slice(0, 200)

  const scored: { option: PickerOption; score: number }[] = []
  for (const option of options) {
    const label = option.label.toLowerCase()
    const id = option.id.toLowerCase()
    let score = -1
    if (label.startsWith(needle) || id.startsWith(needle)) score = 0
    else if (label.includes(needle) || id.includes(needle)) score = 1
    else if (option.keywords.toLowerCase().includes(needle)) score = 2
    else if (isSubsequence(needle, id) || isSubsequence(needle, label)) score = 3
    if (score >= 0) scored.push({ option, score })
  }
  return scored
    .sort((a, b) => a.score - b.score || a.option.label.localeCompare(b.option.label))
    .slice(0, 200)
    .map((entry) => entry.option)
}

function isSubsequence(needle: string, haystack: string): boolean {
  let index = 0
  for (const char of haystack) {
    if (char === needle[index]) index += 1
    if (index === needle.length) return true
  }
  return index === needle.length
}
